import React, { useState } from "react";
import {
  FiCalendar,
  FiFilter,
  FiDownload,
  FiPlus,
} from "react-icons/fi";

import {
  HiOutlineExclamationTriangle,
  HiOutlineShieldExclamation,
  HiOutlineDocumentCheck,
} from "react-icons/hi2";

import { FiClock } from "react-icons/fi";
import IncidentSelection from "./IncidentSelection";
import DateRangePicker from "../../../components/DateRangePicker";
import IncidentFilters from "./IncidentFilters";
import { useIncident } from "../context/incidentContext";

export default function IncidentDashboardHeader() {
  // ============================================================
  // INCIDENT SELECTION MODAL STATE
  // ============================================================

  const [isIncidentSelectionOpen, setIsIncidentSelectionOpen] = useState(false);

  const { range, setRange, summary, loading, error, filtersOpen, setFiltersOpen, exportIncidents, showOpen, setFilters, setPanel } = useIncident();
  const value = number => loading ? '…' : error ? '—' : number;

  const cards = [
    {
      id: 1,
      title: "Open Incidents",
      value: value(summary.open), action: () => showOpen(),
      subtext: "Priority not tracked",
      icon: HiOutlineExclamationTriangle,
      iconBg: "bg-red-600",
      iconColor: "text-white",
    },
    {
      id: 2,
      title: "Near Misses",
      value: "—", action: () => setPanel({ kind: "nearMisses" }),
      subtext: "Not tracked",
      icon: HiOutlineShieldExclamation,
      iconBg: "bg-amber-500",
      iconColor: "text-white",
    },
    {
      id: 3,
      title: "RIDDOR Reportable",
      value: "—", action: () => setPanel({ kind: "riddor" }),
      subtext: "Classification not tracked",
      icon: FiCalendar,
      iconBg: "bg-indigo-600",
      iconColor: "text-white",
    },
    {
      id: 4,
      title: "Overdue (Outstanding)",
      value: value(summary.overdue), action: () => showOpen(true),
      subtext: "Older than 7 days",
      icon: FiClock,
      iconBg: "bg-orange-500",
      iconColor: "text-white",
    },
    {
      id: 5,
      title: "Closed Incidents",
      value: value(summary.completed), action: () => setFilters(previous => ({ ...previous, status: "COMPLETED", olderThan7: false })),
      subtext: "Current status in range",
      subtextIsTrend: false,
      icon: HiOutlineDocumentCheck,
      iconBg: "bg-emerald-600",
      iconColor: "text-white",
    },
  ];

  return (
    <>
      {" "}
      <div className="w-full font-sans antialiased text-[#1e293b]">
        {/* Dashboard Top Header Actions Row */}{" "}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          {" "}
          <div>
            {" "}
            <h1 className="text-2xl font-bold tracking-tight text-[#0f172a]">
              Incident Dashboard{" "}
            </h1>
            <p className="text-sm text-[#64748b] mt-1">
              Report, track and resolve incidents, near misses and recalls to
              ensure safety and compliance.
            </p>
          </div>
          {/* Action Button Controls Row */}
          <div className="flex flex-wrap items-center gap-2.5 self-start lg:self-center">
            <DateRangePicker range={range} setRange={setRange} label="Select incident date range" title="Filter by when the incident occurred. Status and age reflect the current saved record." />

            {/* Filters */}
            <button type="button" onClick={() => setFiltersOpen(!filtersOpen)} aria-expanded={filtersOpen} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#e2e8f0] rounded-lg text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
              <FiFilter className="text-slate-400 w-3.5 h-3.5" />

              <span>Filters</span>
            </button>

            {/* Export */}
            <button type="button" disabled={loading || !!error} onClick={exportIncidents} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#e2e8f0] rounded-lg text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
              <FiDownload className="text-slate-400 w-3.5 h-3.5" />

              <span>Export</span>
            </button>

            {/* REPORT NEW INCIDENT */}
            <button
              type="button"
              onClick={() => setIsIncidentSelectionOpen(true)}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[#0052cc] hover:bg-[#0747a6] text-white rounded-lg text-xs font-semibold shadow-sm transition"
            >
              <FiPlus className="w-3.5 h-3.5 stroke-[2.5]" />

              <span>Report New Incident</span>
            </button>
          </div>
        </div>
        <IncidentFilters />
        {error && <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
        {/* Overview Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {cards.map((card) => {
            const Icon = card.icon;

            return (
              <button type="button" onClick={card.action}
                key={card.id}
                className="text-left bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between min-h-[120px]"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg shrink-0 flex items-center justify-center w-9 h-9 ${card.iconBg}`}
                  >
                    <Icon className={`w-5 h-5 ${card.iconColor}`} />
                  </div>

                  <span className="text-xs font-bold text-slate-800 leading-tight">
                    {card.title}
                  </span>
                </div>

                <div className="mt-3">
                  <span className="text-3xl font-bold text-slate-900 leading-none">
                    {card.value}
                  </span>

                  <div className="text-[11px] font-medium mt-1 tracking-wide">
                    {card.subtextIsTrend ? (
                      <span className="text-emerald-600 flex items-center gap-0.5">
                        {card.subtext}
                      </span>
                    ) : (
                      <span className="text-slate-400">{card.subtext}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* ======================================================
      INCIDENT SELECTION MODAL
  ====================================================== */}
      {isIncidentSelectionOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setIsIncidentSelectionOpen(false)}
          />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
            <IncidentSelection
              onClose={() => setIsIncidentSelectionOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
